// Vincular UCs pela planilha de rateio (projeto 12, demanda 03, #5838).
//
// Abre sempre na PRÉVIA (a RPC com p_aplicar = false não grava nada), mostra a
// contagem por categoria e a lista, e só grava depois de "Confirmar". Quem
// decide cada categoria é a RPC vincular_ucs_por_planilha_rateio; os rótulos e
// a exportação estão em src/lib/vinculoPlanilhaRateio.ts (com testes).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Download, Link2, Loader2, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import {
  EXPLICACAO_CATEGORIA,
  ORDEM_CATEGORIAS,
  ROTULO_CATEGORIA,
  TOM_CATEGORIA,
  contagemOrdenada,
  linhasExportacao,
  rotuloCategoria,
  totalAVincular,
  totalParceiraAVincular,
  type CategoriaVinculo,
  type RelatorioVinculo,
} from '../../../src/lib/vinculoPlanilhaRateio';

interface Props {
  mesReferencia: string;
  onClose: () => void;
}

const COR_TOM = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-300',
  neutro: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300',
  alerta: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-200',
} as const;

const LINHAS_VISIVEIS = 200;

/** O SDK esconde o motivo do erro da RPC em `message`/`details`; mostra o texto do banco. */
const mensagemErro = (e: any) => e?.message || e?.details || 'Erro desconhecido.';

const VincularPlanilhaModal: React.FC<Props> = ({ mesReferencia, onClose }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const podeConfirmar = profile?.role === 'adm' || profile?.role === 'gerente';

  const [relatorio, setRelatorio] = useState<RelatorioVinculo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<CategoriaVinculo | ''>('');
  const [confirmando, setConfirmando] = useState(false);
  const fecharRef = useRef<HTMLButtonElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    const { data, error } = await supabase.rpc('vincular_ucs_por_planilha_rateio', { p_mes_referencia: mesReferencia, p_aplicar: false });
    if (error) {
      setErro(mensagemErro(error));
      setRelatorio(null);
    } else {
      setRelatorio(data as RelatorioVinculo);
    }
    setCarregando(false);
  }, [mesReferencia]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { fecharRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !gravando) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gravando, onClose]);

  const contagem = useMemo(() => contagemOrdenada(relatorio?.contagem as Record<string, number>), [relatorio]);
  const aVincular = useMemo(() => totalAVincular(relatorio?.linhas), [relatorio]);
  const parceiraAVincular = useMemo(() => totalParceiraAVincular(relatorio?.linhas), [relatorio]);
  const linhasFiltradas = useMemo(
    () => (relatorio?.linhas ?? []).filter((l) => !filtro || l.categoria === filtro),
    [relatorio, filtro],
  );

  const exportar = () => {
    if (!relatorio) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(linhasExportacao(relatorio.linhas)), 'Vinculo');
    XLSX.writeFile(wb, `Vinculo_UCs_planilha_${relatorio.mes_referencia.replace('/', '_')}.xlsx`);
  };

  const confirmar = async () => {
    setGravando(true);
    const { data, error } = await supabase.rpc('vincular_ucs_por_planilha_rateio', { p_mes_referencia: mesReferencia, p_aplicar: true });
    setGravando(false);
    setConfirmando(false);
    if (error) {
      addToast(`Nada foi gravado: ${mensagemErro(error)}`, 'error');
      return;
    }
    const r = data as RelatorioVinculo;
    // Só o número devolvido pelo banco confirma quantas UCs foram ligadas.
    addToast(
      r.vinculadas_agora > 0
        ? `${r.vinculadas_agora} UC(s) ligadas às usinas da planilha de ${r.mes_referencia}.`
        : 'Nenhuma UC foi ligada: não havia UC pronta para vincular.',
      r.vinculadas_agora > 0 ? 'success' : 'info',
    );
    await carregar();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="presentation">
      <div role="dialog" aria-modal="true" aria-labelledby="vinculo-titulo"
        className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-6 dark:border-gray-700">
          <div>
            <h2 id="vinculo-titulo" className="flex items-center gap-2 font-poppins text-lg font-bold text-gray-900 dark:text-white">
              <Link2 size={20} className="text-primary" /> Vincular UCs pela planilha de {mesReferencia}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Prévia: nada é gravado até você confirmar. Só entram UCs com cadastro aprovado e sem usina; o resto aparece para conferência.
            </p>
          </div>
          <button ref={fecharRef} onClick={onClose} disabled={gravando} aria-label="Fechar"
            className="rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          {carregando && !relatorio && (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500" role="status">
              <Loader2 className="animate-spin text-primary" size={20} /> Montando a prévia...
            </div>
          )}

          {erro && (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
              <p className="font-semibold">Não foi possível montar a prévia.</p>
              <p className="mt-1">{erro}</p>
              <button onClick={carregar} className="mt-3 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700">Tentar de novo</button>
            </div>
          )}

          {relatorio && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {relatorio.linhas_planilha.toLocaleString('pt-BR')} linha(s) na planilha ·{' '}
                {(relatorio.linhas_planilha - relatorio.linhas_sem_usina).toLocaleString('pt-BR')} com número de usina ·{' '}
                {relatorio.linhas_sem_usina.toLocaleString('pt-BR')} sem usina ("Ativa-S/-Rateio", "Hsolar Crédito" ou vazio), que ficam de fora e não desfazem vínculo.
              </p>

              <section aria-label="Resumo por situação" className="grid grid-cols-2 gap-2 md:grid-cols-3">
                {contagem.map(({ categoria, total }) => {
                  const tom = TOM_CATEGORIA[categoria as CategoriaVinculo] ?? 'alerta';
                  const ativo = filtro === categoria;
                  return (
                    <button key={categoria} type="button" onClick={() => setFiltro(ativo ? '' : (categoria as CategoriaVinculo))} aria-pressed={ativo}
                      className={`rounded-xl border p-3 text-left transition-colors ${COR_TOM[tom]} ${ativo ? 'ring-2 ring-primary' : ''}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide">{rotuloCategoria(categoria)}</p>
                      <p className="text-2xl font-bold tabular-nums">{total.toLocaleString('pt-BR')}</p>
                      <p className="mt-0.5 text-xs opacity-80">{EXPLICACAO_CATEGORIA[categoria as CategoriaVinculo] ?? ''}</p>
                    </button>
                  );
                })}
                {contagem.length === 0 && (
                  <p className="col-span-full text-sm text-gray-500">A planilha deste mês não tem nenhuma linha com número de usina.</p>
                )}
              </section>

              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="vinculo-filtro" className="sr-only">Filtrar por situação</label>
                <select id="vinculo-filtro" value={filtro} onChange={(e) => setFiltro(e.target.value as CategoriaVinculo | '')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                  <option value="">Todas as situações</option>
                  {ORDEM_CATEGORIAS.map((c) => <option key={c} value={c}>{ROTULO_CATEGORIA[c]}</option>)}
                </select>
                <span className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">{linhasFiltradas.length.toLocaleString('pt-BR')} linha(s)</span>
                <button type="button" onClick={exportar}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700">
                  <Download size={15} /> Exportar Excel
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2">UC / Cliente</th>
                      <th className="px-3 py-2">Situação</th>
                      <th className="px-3 py-2">Usina da planilha</th>
                      <th className="px-3 py-2">Usina atual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {linhasFiltradas.slice(0, LINHAS_VISIVEIS).map((l, i) => (
                      <tr key={`${l.uc}-${l.usina_planilha}-${i}`} className="align-top">
                        <td className="px-3 py-2">
                          <p className="font-mono text-xs text-gray-900 dark:text-white">{l.uc ?? '—'}</p>
                          <p className="max-w-[260px] truncate text-xs text-gray-600 dark:text-gray-300" title={l.nome ?? ''}>{l.nome ?? '—'}</p>
                          {l.parceira && <span className="mt-0.5 inline-block rounded bg-blue-100 px-1.5 text-[11px] font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">Empresa parceira</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-200">{rotuloCategoria(l.categoria)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                          <p className="font-mono">{l.usina_planilha}</p>
                          <p>{l.usina_nome ?? (l.usinas_candidatas ?? []).join(' / ') ?? '—'}</p>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">{l.usina_atual_nome ?? '—'}</td>
                      </tr>
                    ))}
                    {linhasFiltradas.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500">Nenhuma linha nesta situação.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {linhasFiltradas.length > LINHAS_VISIVEIS && (
                <p className="text-xs text-gray-500">Mostrando {LINHAS_VISIVEIS} de {linhasFiltradas.length.toLocaleString('pt-BR')}. A lista completa está no Excel.</p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 p-6 dark:border-gray-700">
          {relatorio && !podeConfirmar && (
            <p className="mr-auto text-sm text-gray-500 dark:text-gray-400">Só Gestor (adm ou gerente) pode confirmar o vínculo.</p>
          )}
          {relatorio && podeConfirmar && confirmando && (
            <p className="mr-auto text-sm font-medium text-gray-800 dark:text-gray-100" role="status">
              Ligar {aVincular.toLocaleString('pt-BR')} UC(s) às usinas{parceiraAVincular ? ` (${parceiraAVincular} de empresa parceira)` : ''}? As demais continuam como estão.
            </p>
          )}
          <button onClick={confirmando ? () => setConfirmando(false) : onClose} disabled={gravando}
            className="rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700">
            {confirmando ? 'Voltar' : 'Fechar'}
          </button>
          {podeConfirmar && (
            <button
              onClick={confirmando ? confirmar : () => setConfirmando(true)}
              disabled={!relatorio || aVincular === 0 || gravando || carregando}
              aria-busy={gravando}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {gravando ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {gravando ? 'Vinculando...' : confirmando ? `Confirmar ${aVincular.toLocaleString('pt-BR')}` : aVincular ? `Vincular ${aVincular.toLocaleString('pt-BR')} UC(s)` : 'Nada a vincular'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VincularPlanilhaModal;
